/**
 * OrderFormWizard — Wizard multi-step per la creazione ordine Store.
 *
 * Step 1: Dati azienda
 * Step 2: Selezione prodotto + configurazione endpoint
 * Step 3: Asset da monitorare
 * Step 4: Riepilogo + consenso + submit
 */
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, Send, CreditCard, Building2, ExternalLink } from 'lucide-react';
import StepAziendaData from './StepAziendaData';
import StepProdottoSelect from './StepProdottoSelect';
import StepAssetConfig from './StepAssetConfig';
import StepRiepilogo from './StepRiepilogo';
import StepPagamento from './StepPagamento';
import useTurnstile from './useTurnstile';

const TOTAL_STEPS = 5;

const STEP_LABELS = [
  'Dati Azienda',
  'Scegli Prodotto',
  'Asset',
  'Riepilogo',
  'Pagamento',
];

export default function OrderFormWizard({
  channelInfo,
  codiceCanale,
  onSubmitSuccess,
  prodottiCache,
}) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('bonifico');
  const [serverErrors, setServerErrors] = useState({});

  // Cloudflare Turnstile (configurabile da Django Admin)
  const { enabled: turnstileEnabled, token: turnstileToken, resetTurnstile, TurnstileWidget } = useTurnstile();

  // Dati form
  const [aziendaData, setAziendaData] = useState({
    nome_azienda: '',
    email: '',
    partita_iva: '',
    codice_fiscale: '',
    pec: '',
    url: '',
    telefono: '',
    indirizzo: '',
    cap: '',
    citta: '',
    comune: '',
    provincia: '',
    stato: '',
    numero_dipendenti: '',
    fatturato_annuo: '',
    id_settore: '',
    codice_ateco: '',
  });

  const [ordineData, setOrdineData] = useState({
    listino_prodotto_base: null,
    _bundleId: null,
    numero_endpoint: 1,
    awareness_enabled: false,
    awareness_seats: 0,
    offerta_sorveglianza_rete: '',
    servizio_mssp_monitoring: false,
    gettoni_totali: 0,
    asset: [{ asset: '', tipo_asset: 'dominio' }],
  });

  // Prodotto selezionato + meta add-on (popolato da StepProdottoSelect)
  const [selectedProdotto, setSelectedProdotto] = useState(null);
  const [productMeta, setProductMeta] = useState({ prodottoNS: null, prodottoAwareness: null, prodottoMSSP: null });

  // Callback da StepProdottoSelect per ricevere i prodotti
  const handleProductMeta = useCallback((meta) => {
    if (meta.selectedProdotto) setSelectedProdotto(meta.selectedProdotto);
    setProductMeta({
      prodottoNS: meta.prodottoNS || null,
      prodottoAwareness: meta.prodottoAwareness || null,
      prodottoMSSP: meta.prodottoMSSP || null,
    });
  }, []);

  // Wrapper onChange per Step2
  const handleOrdineChange = useCallback((newData) => {
    setOrdineData(newData);
  }, []);

  // ── Validazione locale per step ────────────────────────────
  const validateStep = () => {
    const errs = {};

    if (step === 1) {
      if (!aziendaData.nome_azienda.trim()) errs['azienda.nome_azienda'] = 'Campo obbligatorio';
      if (!aziendaData.partita_iva.trim()) errs['azienda.partita_iva'] = 'Campo obbligatorio';
      if (!aziendaData.email.trim()) errs['azienda.email'] = 'Campo obbligatorio';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(aziendaData.email))
        errs['azienda.email'] = 'Email non valida';
    }

    if (step === 2) {
      if (!ordineData.listino_prodotto_base && !ordineData._bundleId) errs['listino_prodotto_base'] = 'Seleziona un prodotto';
      if (!ordineData.numero_endpoint || ordineData.numero_endpoint < 1)
        errs['numero_endpoint'] = 'Minimo 1 endpoint';
    }

    if (step === 3) {
      const validAssets = (ordineData.asset || []).filter((a) => a.asset.trim());
      if (validAssets.length === 0) errs['asset'] = 'Inserisci almeno un asset';

      // Controlla duplicati (stesso valore + stesso tipo)
      const seen = new Set();
      for (const a of validAssets) {
        const key = `${a.tipo_asset}:${a.asset.trim().toLowerCase()}`;
        if (seen.has(key)) {
          errs['asset'] = 'Rimuovi gli asset duplicati prima di continuare';
          break;
        }
        seen.add(key);
      }
    }

    if (step === 4) {
      if (!termsAccepted) errs['terms'] = 'Devi accettare i termini';
      if (turnstileEnabled && !turnstileToken) errs['captcha'] = 'Completa la verifica di sicurezza';
    }

    if (step === 5) {
      if (!paymentMethod) errs['payment_method'] = 'Seleziona un metodo di pagamento';
    }

    setServerErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Navigazione ────────────────────────────────────────────
  const goNext = () => {
    if (!validateStep()) return;
    if (step < TOTAL_STEPS) setStep(step + 1);
  };

  const goBack = () => {
    if (step > 1) setStep(step - 1);
  };

  // ── Submit ─────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validateStep()) return;

    setSubmitting(true);
    setServerErrors({});

    // Costruisci payload
    const payload = {
      azienda: {
        nome_azienda: aziendaData.nome_azienda,
        email: aziendaData.email,
        partita_iva: aziendaData.partita_iva || '',
        codice_fiscale: aziendaData.codice_fiscale || '',
        pec: aziendaData.pec || '',
        url: aziendaData.url || '',
        telefono: aziendaData.telefono || '',
        indirizzo: aziendaData.indirizzo || '',
        cap: aziendaData.cap || '',
        citta: aziendaData.citta || '',
        comune: aziendaData.comune || '',
        provincia: aziendaData.provincia || '',
        stato: aziendaData.stato || '',
        numero_dipendenti: aziendaData.numero_dipendenti
          ? parseInt(aziendaData.numero_dipendenti, 10)
          : null,
        fatturato_annuo: aziendaData.fatturato_annuo
          ? parseFloat(aziendaData.fatturato_annuo)
          : null,
        id_settore: aziendaData.id_settore || null,
        codice_ateco: aziendaData.codice_ateco || '',
      },
      ordine: {
        listino_prodotto_base: ordineData._bundleId || ordineData.listino_prodotto_base,
        numero_endpoint: ordineData.numero_endpoint,
        awareness_enabled: ordineData.awareness_enabled,
        awareness_seats: ordineData.awareness_seats,
        offerta_sorveglianza_rete: ordineData.offerta_sorveglianza_rete || '',
        servizio_mssp_monitoring: ordineData.servizio_mssp_monitoring || false,
        gettoni_totali: ordineData.gettoni_totali || 0,
        ore_per_gettone: 2,
        asset: (ordineData.asset || []).filter((a) => a.asset.trim()),
      },
      codice_canale: codiceCanale || '',
      captcha_token: turnstileToken || '',
      payment_method: paymentMethod,
    };

    try {
      const API_BASE = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${API_BASE}/api/store/ordini/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const body = await res.json();

      if (!res.ok) {
        if (body.errors) {
          // Appiattisci errori nested DRF → dot-notation flat
          const flat = {};
          const flatten = (obj, prefix = '') => {
            Object.entries(obj).forEach(([k, v]) => {
              const key = prefix ? `${prefix}.${k}` : k;
              if (v && typeof v === 'object' && !Array.isArray(v)) {
                flatten(v, key);
              } else {
                flat[key] = v;
              }
            });
          };
          flatten(body.errors);
          setServerErrors(flat);

          // Torna allo step appropriato in base all'errore
          if (flat['azienda.email'] || flat['azienda.partita_iva'] || flat['azienda.nome_azienda']) {
            setStep(1);
          } else if (flat['ordine.listino_prodotto_base'] || flat['ordine.asset']) {
            setStep(2);
          } else if (flat['captcha_token']) {
            setStep(4);
            resetTurnstile();
          } else {
            // Errore generico — mostra il primo messaggio disponibile
            const firstMsg = Object.values(flat)[0];
            setServerErrors({ detail: Array.isArray(firstMsg) ? firstMsg[0] : String(firstMsg) });
          }
        } else {
          setServerErrors({ detail: 'Errore durante la creazione dell\'ordine.' });
        }
        return;
      }

      // Successo — se Stripe, redirect a checkout
      if (body.checkout_url) {
        setRedirecting(true);
        window.location.href = body.checkout_url;
        return;
      }
      if (onSubmitSuccess) onSubmitSuccess(body);
    } catch (err) {
      setServerErrors({ detail: 'Errore di connessione. Riprova.' });
      resetTurnstile();
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="max-w-7xl mx-auto h-full flex flex-col">
      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          {STEP_LABELS.map((label, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 text-sm ${
                i + 1 <= step ? 'text-yellow-600 font-semibold' : 'text-gray-400'
              }`}
            >
              <span
                className={`
                  w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
                  ${i + 1 < step
                    ? 'bg-yellow-600 text-white'
                    : i + 1 === step
                      ? 'bg-yellow-100 text-yellow-600 ring-2 ring-yellow-600'
                      : 'bg-gray-200 text-gray-500'}
                `}
              >
                {i + 1 < step ? '✓' : i + 1}
              </span>
              <span className="hidden sm:inline">{label}</span>
            </div>
          ))}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div
            className="bg-yellow-600 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${((step - 1) / (TOTAL_STEPS - 1)) * 100}%` }}
          />
        </div>
      </div>

      {/* Errori globali */}
      {serverErrors.detail && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {serverErrors.detail}
        </div>
      )}

      {/* Step content */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 md:p-6 flex-1 min-h-0 overflow-hidden">
        {step === 1 && (
          <StepAziendaData
            data={aziendaData}
            onChange={setAziendaData}
            errors={serverErrors}
          />
        )}
        {step === 2 && (
          <StepProdottoSelect
            data={ordineData}
            onChange={(newData) => {
              handleOrdineChange(newData);
            }}
            onProductMeta={handleProductMeta}
            errors={serverErrors}
          />
        )}
        {step === 3 && (
          <StepAssetConfig data={ordineData} onChange={setOrdineData} errors={serverErrors} />
        )}
        {step === 4 && (
          <StepRiepilogo
            aziendaData={aziendaData}
            ordineData={ordineData}
            prodotto={selectedProdotto}
            prodottoNS={productMeta.prodottoNS}
            prodottoAwareness={productMeta.prodottoAwareness}
            prodottoMSSP={productMeta.prodottoMSSP}
            channelInfo={channelInfo}
            termsAccepted={termsAccepted}
            onAcceptTerms={setTermsAccepted}
            errors={serverErrors}
            turnstileWidget={TurnstileWidget}
            turnstileEnabled={turnstileEnabled}
          />
        )}
        {step === 5 && (
          <StepPagamento
            paymentMethod={paymentMethod}
            onChangePaymentMethod={setPaymentMethod}
            errors={serverErrors}
            stripeAvailable={!!selectedProdotto?.stripe_price_id}
          />
        )}
      </div>

      {/* Navigation buttons */}
      <div className="flex justify-between mt-3 pb-1">
        {step > 1 ? (
          <button
            type="button"
            onClick={goBack}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Indietro
          </button>
        ) : (
          <button
            type="button"
            onClick={() => navigate(codiceCanale ? `/?ref=${encodeURIComponent(codiceCanale)}` : '/login')}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Indietro
          </button>
        )}

        {step < TOTAL_STEPS ? (
          <button
            type="button"
            onClick={goNext}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-yellow-600 rounded-lg hover:bg-yellow-700 transition-colors"
          >
            Avanti
            <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || redirecting}
            className={`
              flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-colors
              ${redirecting
                ? 'bg-indigo-600 cursor-wait'
                : 'bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-400'}
            `}
          >
            {redirecting ? (
              <>
                <ExternalLink className="w-4 h-4 animate-pulse" />
                Reindirizzamento a Stripe...
              </>
            ) : submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Invio in corso...
              </>
            ) : paymentMethod === 'stripe' ? (
              <>
                <CreditCard className="w-4 h-4" />
                Procedi al Pagamento
              </>
            ) : (
              <>
                <Building2 className="w-4 h-4" />
                Conferma Ordine
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
